import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SideBarMemo as Sidebar } from "./Sidebar";
import { IGif, INiche, INicheMap, IUser } from "services/types";
import { className } from "utils/strings";
import { IAppState } from "store/app-state";

import { MetaInfoMemo as MetaInfo } from "./MetaInfo";
import { VideoComponentMemo as VideoComponent } from "./VideoComponent";
import { GallerySlider } from "./GallerySlider";
import { useGalleryQuery } from "queries";
import PhotoSlider from "./PhotoSlider";
import { FullscreenNavigationMemo as FullscreenNavigation } from "./FullscreenNavigation";
import { metrics, ViewRegion } from "../../utils/metrics";
import { ProgressBar } from "components/UI/ProgressBar";
import "./Player.scss";

const SHOW_TIMEOUT = 100;
const PLAY_TIMEOUT = 300;
const SECONDS = 1000;

const getMode = (gif: IGif, visible: boolean): string => {
  if (visible && gif.type === 1) {
    return "video";
  }

  if (visible) {
    return "image";
  }

  return "empty";
};

interface IProps {
  gif: IGif;
  user: IUser | undefined;
  click: (gif: IGif) => void;
  fullscreen: boolean;
  active: boolean;
  embedded?: boolean;
  hideGif: (id: string) => void;
  toggleFullScreen: (gif: IGif) => void;
  niches?: INicheMap;
  onVisible?: (gif: string, visible: boolean) => void;
  rel?: string;
  position: number;
  source: ViewRegion;
}

export const Player = (props: IProps) => {
  const { gif, toggleFullScreen, niches, onVisible, hideGif } = props;
  const { gifQuality, gifSound } = useSelector((state: IAppState) => state.application);
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [galleryGifId, setGalleryGifId] = useState<string | null>(null);
  const experiments = useSelector((state: IAppState) => state.experiments.experiments);

  const slidePrevArrow = useRef<any>(null);
  const slideNextArrow = useRef<any>(null);
  const sliderPagination = useRef<HTMLDivElement | null>(null);

  // Video progress tracking.
  const [progress, setProgress] = useState<number>(0);
  const [playing, setPlaying] = useState<boolean>(true);
  const [duration, setDuration] = useState<number>(0);

  const gallery = useGalleryQuery(gif?.gallery).data;

  const timeoutVisibleID = useRef<number|null>(null);
  const timeoutPlayID = useRef<number|null>(null);

  const isSentMetrics = useRef(false);

  const isHLS = !!gif.hls && experiments.hls;

  const getVideoUrl = useCallback((): string => {
    let videoUrl: string;

    if (isHLS) {
      const quality = props.fullscreen ? "hd" : gifQuality;

      videoUrl = `${process.env.REACT_APP_API_BASE_URL}v2/gifs/${gif.id}/${quality}.m3u8`;
    } else {
      // @ts-ignore
      videoUrl = props.fullscreen ? (gif.urls.hd || gif.urls.sd) : (gif.urls[gifQuality] || gif.urls.sd);
    }

    return videoUrl;
  }, [props.fullscreen, gif, gifQuality, isHLS]);

  // update video time, seek bar progress, pause video on seek
  const handleSeekBarChange = (time: number): void => {
    if (videoRef.current) {
      videoRef.current.currentTime = time / SECONDS;
    }

    setPlaying(false);
    setProgress(time);
  };

  // play video on seek end
  const handleSeekBarChangeEnd = (): void => setPlaying(true);

  // video duration update
  const handleLoadedMetadata = (): void => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration * SECONDS);
    }
  };

  // update seek bar progress on video playing
  const handleTimeUpdate = (): void => {
    if (videoRef.current) {
      setProgress(videoRef.current.currentTime * SECONDS);
    }
  };

  /**
   * Extract details on niches related to this gif.
   */
  const gifNiches = useMemo(() => {
    if (!niches) {
      return [];
    }

    const items: INiche[] = [];

    if (niches) {
      for (const id of gif.niches) {
        const niche = niches[id];

        if (niche) {
          items.push(niche);
        }
      }
    }

    return items;
  }, [gif.niches, niches]);

  const sendMetrics = useCallback(() => {
    if (!isSentMetrics.current) {
      metrics.sendGifView({
        gifId: gif.id,
        tags: gif.tags,
        fullscreen: props.fullscreen,
        source: props.source,
        position: props.position
      });
      isSentMetrics.current = true;
    }
  }, [gif.id, gif.tags, props.fullscreen, props.position, props.source]);

  const observer = useMemo(() => {
    return new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        if (timeoutVisibleID.current !== null) {
          window.clearTimeout(timeoutVisibleID.current);
          timeoutVisibleID.current = null;
        }

        setIsVisible(false);
        return;
      }

      if (timeoutVisibleID.current !== null) {
        return;
      }

      timeoutVisibleID.current = window.setTimeout(() => {
        setIsVisible(true);
        timeoutVisibleID.current = null;
      }, SHOW_TIMEOUT);
    });
  }, []);

  const o2bserver = useMemo(() => {
    return new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        if (timeoutPlayID.current !== null) {
          window.clearTimeout(timeoutPlayID.current);
          timeoutPlayID.current = null;
        }

        onVisible && onVisible(gif.id, false);
        return;
      }

      if (timeoutPlayID.current !== null) {
        return;
      }

      timeoutPlayID.current = window.setTimeout(() => {
        onVisible && onVisible(gif.id, true);
        timeoutPlayID.current = null;
      }, PLAY_TIMEOUT);
    }, {
      threshold: 0.5,
    });
  }, [gif.id, onVisible]);

  useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current);
      o2bserver.observe(ref.current);

      return () => {
        observer.disconnect();
        o2bserver.disconnect();
      };
    }
    // eslint-disable-next-line
  }, [ref]);

  useEffect(() => {
    if (isVisible && gif.type !== 1) {
      sendMetrics();
    }
  }, [gif.type, isVisible, sendMetrics]);

  const ratio = gif.width / gif.height;
  const height = ratio < 1.0 ? gif.height : gif.width;

  const handleToggleFullScreen = useCallback(() => {
    toggleFullScreen(gif);
  }, [gif, toggleFullScreen]);

  const mode = getMode(gif, isVisible);

  const getImage = useCallback((): string => {
    if (gif.type === 2) {
      // For images, show hd in fs, sd otherwise.
      if (props.fullscreen) {
        return gif.urls.hd ?? gif.urls.sd;
      } else {
        return gif.urls.sd;
      }
    } else {
      // For videos, show the lq thumbnail.
      return gif.urls.thumbnail;
    }
  }, [gif, props.fullscreen]);

  const handlePlay = useCallback(() => {
    sendMetrics();
  }, [sendMetrics]);

  const getId = (id: string) => {
    hideGif(id);
  };

  // Props for the wrapper div.
  const attrs = {
    id: `gif_${gif.id}`,
    className: className({
      active: props.active,
      inactive: !props.active,
      player: true,
      preview: true,
      fullscreen: props.fullscreen,
      wide: ratio > 1.0,
      tall: ratio <= 1.0,
    }),
    style: {
      // eslint-disable-next-line
      aspectRatio: props.fullscreen ? undefined : `${gif.width} / ${height}`,
      height: props.fullscreen ? `${window.innerHeight}px` : "100%",
    },
    ref: ref,
  };

  // Just a place holder for gifs off screen, needed to maintain the scroll position.
  if (mode === "empty") {
    return <div {...attrs}></div>;
  }

  return (
    <div {...attrs}>
      {props.fullscreen && (
        <FullscreenNavigation
          embedded={props.embedded}
          toggleFullScreen={handleToggleFullScreen}
        />
      )}

      <div className="backdropContainer">
        <img
          className="backdrop"
          src={gif.urls.thumbnail}
          alt=""
        />
      </div>

      {mode === "video" && <VideoComponent
        poster={gif.urls.thumbnail}
        video={getVideoUrl()}
        fullscreen={props.fullscreen}
        sound={gifSound}
        canPlay={props.active}
        onPlay={handlePlay}
        videoRef={videoRef}
        playing={playing}
        handleLoadedMetadata={handleLoadedMetadata}
        handleTimeUpdate={handleTimeUpdate}
        isHls={isHLS}
      />}

      {mode === "image" && !gif.gallery && <img
        src={getImage()}
        className="thumbnail"
        alt=""
        style={{
          aspectRatio: `${gif.width} / ${gif.height}`,
        }}
      />}

      {gif.gallery && gallery && gallery.gifs && <GallerySlider
        gallery={gallery.gifs}
        slidePrevArrow={slidePrevArrow}
        slideNextArrow={slideNextArrow}
        sliderPagination={sliderPagination}
        fullscreen={props.fullscreen}
        source={props.source}
        position={props.position}
        setGalleryGifId={setGalleryGifId}
      />}

      <Sidebar
        gif={gif}
        galleryGifId={galleryGifId}
        sound={gifSound}
        fullscreen={props.fullscreen}
        embedded={props.embedded}
        toggleFullScreen={handleToggleFullScreen}
        quality={gifQuality}
        hideGif={getId}
      />

      <div className="metaInfo">
        <MetaInfo
          gif={gif}
          user={props.user}
          niches={gifNiches}
          embedded={!!props.embedded}
          rel={props.rel}
        />
        {(gif.gallery && gallery && gallery.gifs) && (
          <PhotoSlider
            gallery={gallery.gifs}
            slidePrevArrow={slidePrevArrow}
            slideNextArrow={slideNextArrow}
            sliderPagination={sliderPagination}
          />
        )}
      </div>
      {mode === "video" && (
        <div className="ProgressBar">
          <ProgressBar
            max={duration}
            currentTime={progress}
            onChange={handleSeekBarChange}
            onChangeEnd={handleSeekBarChangeEnd}
            fullScreen={props.fullscreen}
          />
        </div>
      )}
    </div>
  );
};
